import { ReactElement } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button as BrandRefreshButton } from '@syf-cl/button'
import routes from 'const/routes'
import { isBrandRefreshEnabled } from 'helpers/getBrandRefreshPhase'
import i18n from 'strings/i18n'
import styled from 'styled-components'
import query from 'syf-component-library/const/mediaQueries'
import { spacing } from 'syf-component-library/defaultTheme'
import Button from 'syf-component-library/ui/atoms/Button'
import { Inline, Inset, Stack } from 'syf-component-library/ui/spacing'
import { Body } from 'syf-component-library/ui/typography'
import { useSetAtom } from 'jotai'
import { modalStatusAtom } from 'store/atoms'

interface ModalFooterProps {
  closeModal: () => void
  hideViewAllActivity?: boolean
}

export const TabsWrapper = styled.div`
  display: flex;

  @media ${query.lessThanSmall} {
    margin: 0 ${spacing.small}px;
  }
`

export const TabsContainer = styled.div`
  width: 100%;
`

export const DrawerHeaderWrapper = styled(Inset)`
  @media ${query.greaterThanSmall} {
    padding: ${spacing.large}px 0 0 ${spacing.xlarge}px;
  }
`
export const HorizontalBorder = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.lightGrey};
`

export const ViewAllActivityMessage = styled(Body)`
  text-align: center;
  max-width: 464px;
  margin: 0 auto;
  color: ${({ theme }) => theme.silver};

  @media ${query.lessThanSmall} {
    max-width: 260px;
  }
`

const FooterWrapper = styled(Inset)`
  display: flex;
  background: ${({ theme }) => theme.lightestGrey};
  flex-direction: column-reverse;
  border-top: 1px solid ${({ theme }) => theme.lightGrey};

  @media ${query.greaterThanSmall} {
    flex-direction: row;
    padding: ${spacing.large}px;
    justify-content: flex-end;
    align-items: center;
  }
`

const FooterButton = styled(Button)`
  margin: 0;
  min-width: 100%;

  @media ${query.greaterThanSmall} {
    min-width: 120px;
  }
`

const ViewAllActivityButton = styled(Button)`
  min-width: 100%;
  padding: 0;
`

const FullWidthButtonWrapper = styled.div`
  width: 100%;

  button {
    width: 100%;
  }

  @media ${query.greaterThanSmall} {
    width: auto;

    button {
      width: auto;
    }
  }
`

export const ModalFooter = ({
  closeModal,
  hideViewAllActivity
}: ModalFooterProps): ReactElement => {
  const updateModalStatus = useSetAtom(modalStatusAtom)
  const navigate = useNavigate()
  const brandRefreshEnabled = isBrandRefreshEnabled()

  const CloseButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : FooterButton
  const closeButtonProps = brandRefreshEnabled
    ? { variant: 'tertiary' }
    : { buttonType: 'primary' }

  const ViewActivityButton: React.ElementType = brandRefreshEnabled
    ? BrandRefreshButton
    : ViewAllActivityButton
  const viewActivityButtonProps = brandRefreshEnabled
    ? { variant: 'primary' }
    : { buttonType: 'text' }

  const handleAccountActivity = () => {
    updateModalStatus('')
    setTimeout(() => {
      navigate(routes.ACTIVITY)
    }, 400)
  }

  const ButtonWrapper = brandRefreshEnabled ? FullWidthButtonWrapper : 'div'

  return (
    <FooterWrapper vertical="base" horizontal="large">
      <ButtonWrapper>
        <CloseButton
          {...closeButtonProps}
          onClick={closeModal}
          data-test="close-button"
          data-type="close"
          data-reason="close"
          data-object="button"
          data-analytics="clicktrackingevent"
        >
          {i18n({ button: 'close' })}
        </CloseButton>
      </ButtonWrapper>
      {!hideViewAllActivity && (
        <>
          <Inline size="xlarge" hideOnBreakpointDown="small" />
          <Stack hideOnBreakpointUp="small" />
          <ButtonWrapper>
            <ViewActivityButton
              {...viewActivityButtonProps}
              data-analytics="clicktrackingevent"
              data-cy="view-all-activity"
              data-object="button"
              data-reason="view all activity"
              data-test="view-all-activity-button"
              data-type="view all activity"
              onClick={() => handleAccountActivity()}
            >
              {i18n({ button: 'viewAllActivity' })}
            </ViewActivityButton>
          </ButtonWrapper>
        </>
      )}
    </FooterWrapper>
  )
}
